import React, {useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import {debounce} from 'lodash';
import {Button, Detail, FlexColumn, FlexRow, Label, Text, TextArea} from '@forecast-it/design-system';
import Util from '../../../../forecast-app/shared/util/util';
import {useForecastFetchQuery} from '../../../../forecast-app/shared/hooks/useForecastFetchQuery';
import {GLOBAL_FILTER_FIELD, GLOBAL_FILTER_OPERATOR} from '../../../../constants';
import RunNovaAgentDryRunMutation from '../../../../mutations/nova/RunNovaAgentDryRunMutation';
import {NovaAgentDraft} from '../state/useNovaAgentDraft';
import {NovaVariable} from '../shared/novaVariableLibrary';
import {SaveState} from '../shared/saveState';
import {NovaStudioColor} from '../shared/novaStudioColors';
import {NovaTestTask} from '../shared/novaTaskVariables';
import {NovaGroundingInputs} from '../shared/novaGroundingBreakdown';
import {toRelayGlobalId} from '../shared/relayGlobalId';
import {NovaModel} from '../shared/novaModelList';
import {NovaDryRunState, NovaRunAgentArguments, NovaRunAgentResult, useNovaDryRun} from '../state/useNovaDryRun';
import {NovaTaskPicker} from './NovaTaskPicker';
import {NovaOutputCard} from './NovaOutputCard';

// Boxed in the Nova tint rather than divided off by a rule: this is the one block on the tab that is
// not an input, so it reads better as its own panel than as a final section of the form above it.
const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 16px;
	border: 1px solid ${NovaStudioColor.novaSurfaceBorder};
	border-radius: 10px;
`;

// The action sits at the trailing edge with its reason beside it, so "why can't I run this" is read on
// the way to the button rather than hunted for underneath it.
const RunRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const RunAction = styled.div`
	margin-left: auto;
`;

// Send sits beside the box it submits rather than under it, so the pair reads as one control.
const FollowUpRow = styled.div`
	display: flex;
	align-items: flex-end;
	gap: 8px;
`;

const FollowUpInput = styled.div`
	flex: 1;
	min-width: 0;
`;

const FOLLOW_UP_HEIGHT = 80;

// Long enough that an ordinary typing burst produces one query rather than one per keystroke, short
// enough that the list still feels like it is keeping up.
const SEARCH_DEBOUNCE_MS = 250;

// Why the agent cannot be run right now. Null means it can.
export const NovaTestBlockedReason = {
	// There are unsaved edits, and a dry run reads the saved definition.
	Unsaved: 'UNSAVED',
	// One of the two required inputs is empty, so there is nothing coherent to run.
	MissingInputs: 'MISSING_INPUTS',
} as const;
export type NovaTestBlockedReason = (typeof NovaTestBlockedReason)[keyof typeof NovaTestBlockedReason];

const BLOCKED_MESSAGE_ID: Record<NovaTestBlockedReason, string> = {
	[NovaTestBlockedReason.Unsaved]: 'nova.test.blockedUnsaved',
	[NovaTestBlockedReason.MissingInputs]: 'nova.test.blockedMissingInputs',
};

// A dry run must run what production would run — the saved record — so unsaved edits block it. Both
// required inputs must also be present; an agent missing either is not yet an agent that can be run
// at all. The optional agent.md is not required.
//
// Unsaved is reported ahead of missing inputs: it is the condition the author just caused, and saving
// may well resolve the other one too.
export function novaTestBlockedReason(
	inputs: NovaGroundingInputs,
	isDirty: boolean,
	saveState: SaveState
): NovaTestBlockedReason | null {
	if (isDirty || saveState !== SaveState.Saved) {
		return NovaTestBlockedReason.Unsaved;
	}
	if (inputs.systemPrompt.trim() === '' || inputs.userPromptTemplate.trim() === '') {
		return NovaTestBlockedReason.MissingInputs;
	}
	return null;
}

export function isNovaTestable(inputs: NovaGroundingInputs, isDirty: boolean, saveState: SaveState): boolean {
	return novaTestBlockedReason(inputs, isDirty, saveState) === null;
}

export const novaTestTasksQuery = graphql`
	query NovaTestSection_Query($searchQuery: TaskSearchQueryType) {
		viewer {
			company {
				# A page of matches, not the whole company: the count reaches the task service as its
				# query limit, so this bounds the database work rather than trimming a large result
				# afterwards. Relay needs a literal here, so it cannot be a named constant.
				allTasks(first: 100, searchQuery: $searchQuery) {
					edges {
						node {
							id
							companyTaskId
							name
							description
							fullAccessToProject
							project {
								name
								description
								client {
									name
								}
							}
							files(first: 50) {
								edges {
									node {
										id
										name
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;

// Matches across every task in the company, narrowing on the author's search text.
//
// The text goes to the server rather than being filtered in the client, because the client only ever
// holds one page: filtering locally would silently hide any task outside it. The task service turns
// a NAME filter into a name match plus, when the text looks like a task number, a match on that —
// so `T113` and `landing page` both work without any of it being reimplemented here.
//
// Done tasks are excluded: an agent is tested against live work.
export function novaTestTasksSearchQuery(searchText: string) {
	const filters: {field: string; operator: string; value: string[]}[] = [
		{field: GLOBAL_FILTER_FIELD.STATUS, operator: GLOBAL_FILTER_OPERATOR.IS, value: ['TODO', 'INPROGRESS']},
	];
	const trimmed = searchText.trim();
	if (trimmed !== '') {
		filters.push({field: GLOBAL_FILTER_FIELD.NAME, operator: GLOBAL_FILTER_OPERATOR.CONTAINS, value: [trimmed]});
	}
	return {filters};
}

interface NovaTestTaskEdge {
	node: {
		id: string;
		companyTaskId: number | null;
		name: string | null;
		description: string | null;
		fullAccessToProject: boolean | null;
		project: {
			name: string | null;
			description: string | null;
			client: {name: string | null} | null;
		} | null;
		files: {edges: {node: {id: string; name: string | null}}[]} | null;
	};
}

interface NovaTestTasksResponse {
	viewer: {company: {allTasks: {edges: NovaTestTaskEdge[]}}};
}

// Flattens one Relay task node into the plain shape everything downstream works with, so no other
// module has to know about connections, edges, or which of these fields the schema calls nullable.
export function toTestTask(edge: NovaTestTaskEdge): NovaTestTask {
	const node = edge.node;
	const project = node.project;
	const files = node.files;
	return {
		taskId: node.id,
		companyTaskId: node.companyTaskId,
		name: node.name ?? '',
		description: node.description,
		projectName: project === null ? null : project.name,
		projectDescription: project === null ? null : project.description,
		clientName: project === null || project.client === null ? null : project.client.name,
		fileNames: files === null ? [] : files.edges.map(fileEdge => fileEdge.node.name ?? '').filter(name => name !== ''),
	};
}

interface Props {
	agentId: string;
	agentName: string;
	draft: NovaAgentDraft;
	variables: NovaVariable[];
	// Needed only so a reference to a retired variable reads as broken rather than as a name that
	// never existed — the same reason the editors above take it.
	removedVariables: NovaVariable[];
	// Passed through to the output card, which names the model each run answered on.
	models: NovaModel[];
}

// Points a saved agent at a real task and runs it as a dry run: the same three inputs at the same two
// trust tiers production will use, with every result-routing side-effect skipped, so nothing is ever
// written to the customer's task.
//
// The section stays on screen while the prompts above it are edited — only Run is disabled, with the
// reason named. Collapsing it would reflow the page on the first keystroke and take away the previous
// run's breakdown at exactly the moment the author is editing in response to it.
export function NovaTestSection({agentId, agentName, draft, variables, removedVariables, models}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	const [followUpText, setFollowUpText] = useState('');
	const [searchText, setSearchText] = useState('');
	const {data, fetch, loading} = useForecastFetchQuery<NovaTestTasksResponse, {searchQuery: unknown}>(novaTestTasksQuery);

	const inputs: NovaGroundingInputs = draft.promptState.values;
	const blockedReason = novaTestBlockedReason(inputs, draft.isDirty, draft.saveState);

	const fetchTasks = useCallback(
		(text: string) => {
			fetch({searchQuery: novaTestTasksSearchQuery(text)});
		},
		// `fetch` is rebuilt every render by useForecastFetchQuery, so depending on it would re-run
		// this on every render.
		[]
	);

	// One debounced instance for the component's lifetime; rebuilding it per render would defeat the
	// debounce entirely, and it is cancelled on unmount so a late query cannot set state afterwards.
	const debouncedFetchTasks = useMemo(() => debounce(fetchTasks, SEARCH_DEBOUNCE_MS), [fetchTasks]);
	useEffect(() => () => debouncedFetchTasks.cancel(), [debouncedFetchTasks]);

	// The first page is fetched on mount so the list is populated before the author types a thing.
	// Deliberately not gated on whether the agent is runnable: the prompts are edited right above
	// this, so waiting for a clean draft would mean the list is usually still empty at the moment it
	// becomes usable.
	useEffect(() => {
		fetchTasks('');
	}, [fetchTasks]);

	const handleSearchTextChange = useCallback(
		(text: string) => {
			setSearchText(text);
			debouncedFetchTasks(text);
		},
		[debouncedFetchTasks]
	);

	const tasks = useMemo(() => {
		if (data === null || data === undefined) {
			return [];
		}
		return (
			data.viewer.company.allTasks.edges
				// A task on a project the author cannot fully see would assemble context they are not
				// entitled to read — the same guard the My Work task lists apply.
				.filter(edge => edge.node.fullAccessToProject !== false)
				.map(toTestTask)
		);
	}, [data]);

	const runAgent = useCallback(
		({taskId, message, threadId}: NovaRunAgentArguments) =>
			new Promise<NovaRunAgentResult>((resolve, reject) => {
				Util.CommitMutation(
					RunNovaAgentDryRunMutation,
					// `agentId` is the raw uuid the catalogue decodes out of the node id, but the mutation
					// resolver runs `fromGlobalId` on it — so it has to go back over the wire encoded, the
					// same way every other Nova call site sends it. `taskId` is already a global id.
					// `threadId` is neither: the record serves it as its own plain string, so it goes back
					// exactly as it came.
					{id: toRelayGlobalId('NovaAgent', agentId), taskId, message, threadId},
					(response: {runNovaAgentDryRun: {result: NovaRunAgentResult}}) =>
						resolve(response.runNovaAgentDryRun.result),
					true,
					null,
					// This section renders its own error state with a retry, so the shared modal would be
					// a second report of one failure. Rejecting hands it to the hook, which owns that.
					(error: Error) => reject(error)
				);
			}),
		[agentId]
	);

	const dryRun = useNovaDryRun({
		inputs,
		validationContext: {offered: variables, removed: removedVariables},
		runAgent,
	});

	// react-stately will not put the chosen task's name into the box for us. The picker controls both
	// `inputValue` and `selectedKey`, and useComboBoxState explicitly skips its own input-reset in that
	// case ("it's the user's responsibility to update inputValue in onSelectionChange"). Without this
	// the task is selected in state while the field still shows whatever was typed, which reads as the
	// pick not taking at all.
	//
	// setSearchText directly rather than through handleSearchTextChange: that one also kicks off a
	// debounced search, which would narrow the list to the task just chosen and leave the author
	// unable to reopen the dropdown and switch to a different one.
	const handleSelectTask = useCallback(
		(chosen: NovaTestTask) => {
			dryRun.selectTask(chosen);
			setSearchText(chosen.name);
		},
		[dryRun]
	);

	const handleFollowUp = useCallback(() => {
		const message = followUpText.trim();
		if (message === '') {
			return;
		}
		setFollowUpText('');
		dryRun.followUp(message);
	}, [followUpText, dryRun]);

	const canRun = blockedReason === null && dryRun.canRun;

	return (
		<Section data-testid="nova-test-section" data-cy="nova-test-section">
			<Label>{formatMessage({id: 'nova.test.sectionTitle'})}</Label>
			<NovaTaskPicker
				tasks={tasks}
				isLoading={loading}
				selectedTask={dryRun.selectedTask}
				searchText={searchText}
				onSearchTextChange={handleSearchTextChange}
				onSelectTask={handleSelectTask}
				onClearTask={dryRun.clearTask}
				dataCy="nova-task-picker"
			/>
			<RunRow>
				{blockedReason === null ? null : (
					<Detail data-testid="nova-test-blocked" data-cy="nova-test-blocked">
						{formatMessage({id: BLOCKED_MESSAGE_ID[blockedReason]})}
					</Detail>
				)}
				<RunAction>
					<Button data-cy="nova-test-run" disabled={!canRun} onPress={dryRun.run}>
						{formatMessage({id: 'nova.test.runAction'})}
					</Button>
				</RunAction>
			</RunRow>
			{dryRun.state === NovaDryRunState.Running ? (
				<Detail data-testid="nova-test-running" data-cy="nova-test-running">
					{formatMessage({id: 'nova.test.running'})}
				</Detail>
			) : null}
			{dryRun.state === NovaDryRunState.Error ? (
				<FlexColumn gap="s">
					<div data-testid="nova-test-error" data-cy="nova-test-error">
						<Text>{formatMessage({id: 'nova.test.errorTitle'})}</Text>
						<Detail>{formatMessage({id: 'nova.test.errorBody'})}</Detail>
					</div>
					<FlexRow>
						<Button data-cy="nova-test-retry" onPress={dryRun.retry}>
							{formatMessage({id: 'nova.test.retryAction'})}
						</Button>
					</FlexRow>
				</FlexColumn>
			) : null}
			{dryRun.turns.map((turn, index) => (
				<NovaOutputCard
					key={`${turn.ranAt}-${index}`}
					agentName={agentName}
					taskTitle={dryRun.selectedTask === null ? '' : dryRun.selectedTask.name}
					turn={turn}
					models={models}
					dataCy={`nova-test-turn-${index}`}
				/>
			))}
			{dryRun.turns.length > 0 ? (
				<FlexColumn gap="s">
					<FollowUpRow>
						<FollowUpInput>
							<TextArea
								data-cy="nova-test-follow-up"
								value={followUpText}
								onChange={(changed: string) => setFollowUpText(changed)}
								onBlur={() => undefined}
								isOptional={false}
								placeholder={formatMessage({id: 'nova.test.followUpPlaceholder'})}
								height={FOLLOW_UP_HEIGHT}
								width={TextArea.WIDTH.FLEX}
							/>
						</FollowUpInput>
						<Button data-cy="nova-test-follow-up-send" disabled={!canRun} onPress={handleFollowUp}>
							{formatMessage({id: 'nova.test.followUpAction'})}
						</Button>
					</FollowUpRow>
					<FlexRow>
						<Button data-cy="nova-test-clear-conversation" emphasis="low" onPress={dryRun.clearConversation}>
							{formatMessage({id: 'nova.test.clearConversation'})}
						</Button>
					</FlexRow>
				</FlexColumn>
			) : null}
		</Section>
	);
}
